/**
 * lc-pdp-wishlist.js
 * Save-for-later wishlist with localStorage primary store and best-effort
 * customer-bound persistence for signed-in users (BD-19 (i)).
 *
 * localStorage key: lc_saved
 * Value: JSON array of { handle, title, image, url }
 *
 * Customer-bound storage (logged in, NCA): metafield
 *   custom.lc_saved (List of single-line text — handles only).
 * Liquid injects the customer's handles via snippet
 *   lc-saved-customer-config so the script can merge cloud → device on
 * load. Write-back (device → cloud) is best-effort via Customer Account
 * API; theme JS has no first-class write path under NCA, so this is
 * expected to fail without a configured app proxy. See architecture
 * doc at findings/c2-saved/saved-architecture.md for the full story.
 *
 * PDP usage:
 *   Bookmark button carries data attributes:
 *     data-wl-handle, data-wl-title, data-wl-image, data-wl-url
 *
 * Saved page usage:
 *   Call LCWishlist.renderSavedPage(containerEl) to render saved items.
 */

(function () {
  'use strict';

  var STORAGE_KEY = 'lc_saved';

  var LCWishlist = {
    /**
     * Return the current saved list from localStorage.
     * @returns {Array}
     */
    getAll: function () {
      try {
        var raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return [];
        var parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
      } catch (_e) {
        return [];
      }
    },

    /**
     * Persist the list to localStorage.
     * @param {Array} list
     */
    _save: function (list) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
      } catch (_e) {
        // Quota or private-mode error — fail silently.
      }
    },

    /**
     * Check whether a product handle is saved.
     * @param {string} handle
     * @returns {boolean}
     */
    isSaved: function (handle) {
      return this.getAll().some(function (item) {
        return item.handle === handle;
      });
    },

    /**
     * Add a product to the saved list (no-op if already present).
     * @param {{ handle: string, title: string, image: string, url: string }} product
     */
    add: function (product) {
      var list = this.getAll();
      var exists = list.some(function (item) {
        return item.handle === product.handle;
      });
      if (!exists) {
        list.push({
          handle: product.handle,
          title: product.title,
          image: product.image,
          url: product.url
        });
        this._save(list);
      }
    },

    /**
     * Remove a product from the saved list.
     * @param {string} handle
     */
    remove: function (handle) {
      var list = this.getAll().filter(function (item) {
        return item.handle !== handle;
      });
      this._save(list);
    },

    /**
     * Toggle saved state. Returns true if now saved, false if now removed.
     * @param {{ handle: string, title: string, image: string, url: string }} product
     * @returns {boolean}
     */
    toggle: function (product) {
      if (this.isSaved(product.handle)) {
        this.remove(product.handle);
        return false;
      } else {
        this.add(product);
        return true;
      }
    },

    // ─── Customer-bound persistence (BD-19 (i)) ────────────────────────────

    /**
     * Read the injected customer config (customerId, shopId, saved handles)
     * from #lc-saved-customer-config. Returns null when anonymous or when
     * the snippet did not render.
     */
    getCustomerConfig: function () {
      var el = document.getElementById('lc-saved-customer-config');
      if (!el) return null;
      try {
        var cfg = JSON.parse(el.textContent);
        if (!cfg || !cfg.customerId) return null;
        return cfg;
      } catch (_e) {
        return null;
      }
    },

    /**
     * @returns {boolean} true when a customer is signed in via NCA.
     */
    isLoggedIn: function () {
      return !!this.getCustomerConfig();
    },

    /**
     * Read the cloud-side saved-handles list from the injected customer
     * config. List metafields of type single_line_text_field surface as
     * arrays via Liquid | json; null when the customer has no saves.
     * @returns {string[]}
     */
    getCloudHandles: function () {
      var cfg = this.getCustomerConfig();
      if (!cfg) return [];
      var raw = cfg.savedHandles;
      if (Array.isArray(raw)) {
        return raw.filter(function (h) { return typeof h === 'string' && h; });
      }
      if (typeof raw === 'string' && raw) {
        try {
          var parsed = JSON.parse(raw);
          if (Array.isArray(parsed)) {
            return parsed.filter(function (h) { return typeof h === 'string' && h; });
          }
        } catch (_e) {}
      }
      return [];
    },

    /**
     * Read the storefront API config (token + api version) from
     * #lc-saved-config. Only rendered on /pages/saved at present, so
     * returns null in PDP context — callers should fall back to stubs.
     */
    _getStorefrontConfig: function () {
      var el = document.getElementById('lc-saved-config');
      if (!el) return null;
      try {
        var cfg = JSON.parse(el.textContent);
        if (!cfg || !cfg.storefrontAccessToken) return null;
        return cfg;
      } catch (_e) {
        return null;
      }
    },

    /**
     * Resolve a list of product handles to { handle, title, image, url }
     * via the Storefront API. Calls back with a map keyed by handle.
     * Failure / no-token / handle-not-found results in an empty map for
     * that handle; caller falls back to a minimal stub.
     */
    _fetchProductStubs: function (handles, callback) {
      var cfg = this._getStorefrontConfig();
      if (!cfg || !handles.length) {
        callback({});
        return;
      }
      var aliasFragments = handles.map(function (h, i) {
        // product(handle:) singular, not products(query:) — the latter
        // does not exact-match on handle (verified 2026-05-17).
        return 'p' + i + ': product(handle: ' + JSON.stringify(h) + ') {' +
          ' handle title featuredImage { url }' +
        ' }';
      });
      var body = JSON.stringify({ query: 'query SavedStubs {' + aliasFragments.join(' ') + '}' });
      fetch('/api/' + cfg.apiVersion + '/graphql.json', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': cfg.storefrontAccessToken
        },
        body: body
      }).then(function (r) {
        return r.ok ? r.json() : Promise.reject(new Error('HTTP ' + r.status));
      }).then(function (data) {
        var stubs = {};
        if (data && data.data) {
          Object.keys(data.data).forEach(function (key) {
            var n = data.data[key];
            if (n && n.handle) {
              stubs[n.handle] = {
                handle: n.handle,
                title: n.title || n.handle,
                image: n.featuredImage && n.featuredImage.url ? n.featuredImage.url : '',
                url: '/products/' + n.handle
              };
            }
          });
        }
        callback(stubs);
      }).catch(function () {
        callback({});
      });
    },

    /**
     * Merge cloud-side saved handles into localStorage. Union semantics:
     * anything in the cloud that isn't already on this device gets added.
     * Local-only items remain (they will be pushed up by pushToCustomer).
     *
     * For new-from-cloud handles, resolves title/image via Storefront API
     * when the token is available (saved page). On PDP context where the
     * token isn't injected, stores a minimal stub so the bookmark button
     * still reads the correct saved state — full metadata fills in on
     * the next visit to /pages/saved.
     *
     * @param {Function} [done] called when merge completes.
     */
    syncFromCustomer: function (done) {
      var finish = typeof done === 'function' ? done : function () {};
      var cloud = this.getCloudHandles();
      if (!cloud.length) { finish(); return; }

      var localList = this.getAll();
      var localHandles = {};
      localList.forEach(function (item) { if (item && item.handle) localHandles[item.handle] = true; });

      var missing = cloud.filter(function (h) { return !localHandles[h]; });
      if (!missing.length) { finish(); return; }

      var self = this;
      this._fetchProductStubs(missing, function (stubs) {
        var merged = localList.slice();
        missing.forEach(function (h) {
          if (stubs[h]) {
            merged.push(stubs[h]);
          } else {
            merged.push({ handle: h, title: h, image: '', url: '/products/' + h });
          }
        });
        self._save(merged);
        finish();
      });
    },

    /**
     * Best-effort push of the current localStorage handle list to the
     * customer metafield via Customer Account API. Theme JS has no
     * first-class write path under New Customer Accounts (NCA bypasses
     * Liquid customer auth; CORS + OAuth scope blockers on direct
     * Customer Account API calls without a configured app proxy).
     *
     * We attempt two candidate endpoints. On all-attempt failure we log
     * info-level and return; local saves still persist. Cross-device
     * write propagation is documented as a follow-up in
     * findings/c2-saved/saved-architecture.md.
     */
    pushToCustomer: function () {
      var cfg = this.getCustomerConfig();
      if (!cfg) return;

      var handles = this.getAll().map(function (i) { return i.handle; }).filter(Boolean);
      var mutation = 'mutation Push($input: CustomerUpdateInput!) {' +
        ' customerUpdate(input: $input) { userErrors { field message } }' +
      ' }';
      var body = JSON.stringify({
        query: mutation,
        variables: {
          input: {
            metafields: [{
              namespace: 'custom',
              key: 'lc_saved',
              type: 'list.single_line_text_field',
              value: JSON.stringify(handles)
            }]
          }
        }
      });

      var endpoints = ['/account/customer/api/2025-04/graphql.json'];
      if (cfg.shopId) {
        endpoints.push('https://shopify.com/' + cfg.shopId + '/account/customer/api/2025-04/graphql');
      }

      var logged = false;
      function logSkip() {
        if (logged) return;
        logged = true;
        if (window.console && console.info) {
          console.info('[lc-saved] customer metafield write-back unavailable from theme JS under NCA — saves persist locally; cross-device sync requires future backend.');
        }
      }

      function attempt(i) {
        if (i >= endpoints.length) { logSkip(); return; }
        try {
          fetch(endpoints[i], {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: body
          }).then(function (r) {
            if (!r.ok) { attempt(i + 1); return null; }
            return r.json();
          }).then(function (data) {
            if (!data) return;
            var result = data.data && data.data.customerUpdate;
            var ok = result && (!result.userErrors || !result.userErrors.length);
            if (!ok) attempt(i + 1);
          }).catch(function () { attempt(i + 1); });
        } catch (_e) {
          attempt(i + 1);
        }
      }
      attempt(0);
    }
  };

  // Expose on window for access from other scripts (e.g. page-saved section).
  window.LCWishlist = LCWishlist;

  // ─── PDP bookmark button ───────────────────────────────────────────────────

  function initBookmark() {
    var btn = document.getElementById('lc-pdp-wishlist-btn');
    if (!btn) return;

    var handle = btn.dataset.wlHandle;
    var title  = btn.dataset.wlTitle;
    var image  = btn.dataset.wlImage;
    var url    = btn.dataset.wlUrl;

    function applyState() {
      updateBtnState(btn, LCWishlist.isSaved(handle));
    }

    // Initial state. If the customer is signed in, merge cloud handles
    // into localStorage first so a print saved on another device shows
    // "Saved" instead of "Save" when the buyer lands here.
    if (LCWishlist.isLoggedIn()) {
      LCWishlist.syncFromCustomer(applyState);
    } else {
      applyState();
    }

    btn.addEventListener('click', function () {
      var nowSaved = LCWishlist.toggle({ handle: handle, title: title, image: image, url: url });
      updateBtnState(btn, nowSaved);
      if (LCWishlist.isLoggedIn()) {
        LCWishlist.pushToCustomer();
      }
    });
  }

  /**
   * Update the visual and accessible state of the bookmark button.
   * Text-based: swaps the label between "Save" and "Saved" and renders
   * a leading checkmark glyph in the saved state.
   * @param {HTMLElement} btn
   * @param {boolean} saved
   */
  function updateBtnState(btn, saved) {
    var glyph = btn.querySelector('.lc-catalogue-band__gesture-glyph');
    var label = btn.querySelector('.lc-catalogue-band__gesture-label');

    if (saved) {
      btn.setAttribute('aria-label', 'Saved');
      btn.setAttribute('aria-pressed', 'true');
      btn.classList.add('is-saved');
      if (glyph) glyph.textContent = '\u2713';
      if (label) label.textContent = 'Saved';
    } else {
      btn.setAttribute('aria-label', 'Save for later');
      btn.setAttribute('aria-pressed', 'false');
      btn.classList.remove('is-saved');
      if (glyph) glyph.textContent = '';
      if (label) label.textContent = 'Save';
    }
  }

  // ─── Saved page renderer ──────────────────────────────────────────────────

  /**
   * Render saved items into a container element on the /saved page.
   * @param {HTMLElement} container
   */
  LCWishlist.renderSavedPage = function (container) {
    if (!container) return;

    var list = this.getAll();
    container.innerHTML = '';

    if (list.length === 0) {
      var empty = document.createElement('div');
      empty.className = 'lc-saved__empty';

      var emptyP1 = document.createElement('p');
      emptyP1.className = 'lc-saved__empty-body';
      emptyP1.textContent = "You haven't saved any prints yet. Saved prints are held in your browser, so you can come back to them without a sign-in.";
      empty.appendChild(emptyP1);

      var emptyP2 = document.createElement('p');
      emptyP2.className = 'lc-saved__empty-body';
      emptyP2.textContent = "When you find one you like, tap Save on the print page. It'll show up here.";
      empty.appendChild(emptyP2);

      var ctas = document.createElement('div');
      ctas.className = 'lc-saved__empty-ctas';

      var cta1 = document.createElement('a');
      cta1.className = 'lc-btn lc-btn--ghost lc-saved__empty-cta';
      cta1.href = '/collections/all';
      cta1.textContent = 'Browse the collection →';
      ctas.appendChild(cta1);

      empty.appendChild(ctas);
      container.appendChild(empty);
      return;
    }

    var ul = document.createElement('ul');
    ul.className = 'lc-saved__list';

    list.forEach(function (item) {
      var li = document.createElement('li');
      li.className = 'lc-saved__item';

      var imgSrc = item.image
        ? item.image.replace(/^\/\//, 'https://')
        : '';

      // Build <a> via DOM API so href is set as a property (safe, no XSS from
      // malformed localStorage data — unlike string concatenation through encodeURI
      // which does not escape `"` and is vulnerable to quote-break injection).
      var a = document.createElement('a');
      a.className = 'lc-saved__link';
      a.href = item.url;

      var imgWrap = document.createElement('div');
      imgWrap.className = 'lc-saved__img-wrap';

      if (imgSrc) {
        var img = document.createElement('img');
        img.className = 'lc-saved__img';
        img.src = imgSrc;
        img.alt = item.title;
        img.width = 200;
        img.height = 200;
        img.loading = 'lazy';
        imgWrap.appendChild(img);
      } else {
        var placeholder = document.createElement('div');
        placeholder.className = 'lc-saved__img lc-saved__img--placeholder';
        placeholder.setAttribute('aria-hidden', 'true');
        imgWrap.appendChild(placeholder);
      }

      // Static badge hooks. Dynamic edition-count enrichment lands in
      // LOS7-280; until then these stay hidden. Two slots: scarcity
      // ("X of Y remaining") and sold-through.
      var remainingBadge = document.createElement('span');
      remainingBadge.className = 'lc-saved__badge lc-saved__badge--remaining';
      remainingBadge.dataset.handle = item.handle;
      remainingBadge.hidden = true;
      imgWrap.appendChild(remainingBadge);

      var soldThroughBadge = document.createElement('span');
      soldThroughBadge.className = 'lc-saved__badge lc-saved__badge--soldthrough';
      soldThroughBadge.dataset.handle = item.handle;
      soldThroughBadge.textContent = 'Sold through';
      soldThroughBadge.hidden = true;
      imgWrap.appendChild(soldThroughBadge);

      a.appendChild(imgWrap);

      // Card info block — eyebrow + title + price + variant.
      // Wrapped so internal margins control text spacing without
      // inheriting the parent flex gap (which sets the image→text
      // distance). BD-19 (iii) card enrichment.
      var info = document.createElement('div');
      info.className = 'lc-saved__info';

      // Series eyebrow — populated by lc-saved-enrichment.js (BD-10 universal).
      // Hidden until the enrichment fetch resolves the product's
      // series_reference metafield. Class shared with canonical
      // lc-card-eyebrow snippet so styling cascades from lc-cell.css.
      var eyebrowSpan = document.createElement('span');
      eyebrowSpan.className = 'lc-card-eyebrow lc-card-eyebrow--saved lc-saved__eyebrow';
      eyebrowSpan.dataset.handle = item.handle;
      eyebrowSpan.hidden = true;
      info.appendChild(eyebrowSpan);

      var titleSpan = document.createElement('span');
      titleSpan.className = 'lc-saved__title';
      titleSpan.textContent = item.title;
      info.appendChild(titleSpan);

      // Price + variant slots. Variant title ("M / Framed / Black timber")
      // is omitted for single-variant products and for "Default Title".
      var priceSpan = document.createElement('span');
      priceSpan.className = 'lc-saved__price';
      priceSpan.dataset.handle = item.handle;
      priceSpan.hidden = true;
      info.appendChild(priceSpan);

      var variantSpan = document.createElement('span');
      variantSpan.className = 'lc-saved__variant';
      variantSpan.dataset.handle = item.handle;
      variantSpan.hidden = true;
      info.appendChild(variantSpan);

      a.appendChild(info);

      li.appendChild(a);

      var actions = document.createElement('div');
      actions.className = 'lc-saved__actions';

      var viewLink = document.createElement('a');
      viewLink.className = 'lc-btn lc-btn--ghost lc-saved__action lc-saved__action--view';
      viewLink.href = item.url;
      viewLink.textContent = 'View';
      actions.appendChild(viewLink);

      var removeBtn = document.createElement('button');
      removeBtn.className = 'lc-btn lc-btn--ghost lc-saved__action lc-saved__action--remove';
      removeBtn.type = 'button';
      removeBtn.dataset.handle = item.handle;
      removeBtn.setAttribute('aria-label', 'Remove ' + item.title + ' from saved prints');
      removeBtn.textContent = 'Remove';
      actions.appendChild(removeBtn);

      li.appendChild(actions);
      ul.appendChild(li);
    });

    container.appendChild(ul);

    container.querySelectorAll('.lc-saved__action--remove').forEach(function (removeBtn) {
      removeBtn.addEventListener('click', function () {
        var h = removeBtn.dataset.handle;
        LCWishlist.remove(h);
        LCWishlist.renderSavedPage(container);
        if (LCWishlist.isLoggedIn()) {
          LCWishlist.pushToCustomer();
        }
      });
    });
  };

  function _escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // ─── Saved page init ──────────────────────────────────────────────────────

  /**
   * Initialise the saved page if the container element is present.
   * Called from the DOMContentLoaded handler so that LCWishlist is guaranteed
   * to be defined before renderSavedPage runs — avoids the race where the
   * deferred script hasn't executed yet when an inline script fires first.
   *
   * For signed-in customers, syncs cloud handles into localStorage before
   * first render so anything saved on another device appears immediately.
   * Storefront API token is present here (lc-saved-config), so titles +
   * images for new-from-cloud handles resolve to real product data
   * rather than handle stubs.
   */
  function initSavedPage() {
    var container = document.getElementById('lc-saved-container');
    if (!container) return;
    if (LCWishlist.isLoggedIn()) {
      LCWishlist.syncFromCustomer(function () {
        LCWishlist.renderSavedPage(container);
      });
    } else {
      LCWishlist.renderSavedPage(container);
    }
  }

  // ─── Share button ─────────────────────────────────────────────────────────

  function initShare() {
    var btn = document.getElementById('lc-pdp-share-btn');
    if (!btn) return;

    var tooltip = document.getElementById('lc-pdp-share-tooltip');
    var shareTitle = btn.dataset.shareTitle || document.title;
    var shareUrl   = window.location.href;

    var tooltipTimer = null;
    var tooltipClearTimer = null;

    function showTooltip() {
      if (!tooltip) return;
      clearTimeout(tooltipTimer);
      clearTimeout(tooltipClearTimer);
      // Set textContent at show time (not in markup) so screen readers
      // reading the aria-live region at page load don't announce "Copied!"
      // when nothing has happened yet.
      tooltip.textContent = 'Copied!';
      tooltip.classList.add('lc-pdp-share-tooltip--visible');
      tooltipTimer = setTimeout(function () {
        tooltip.classList.remove('lc-pdp-share-tooltip--visible');
        // Clear after the opacity fade completes so the live region returns
        // to empty between events.
        tooltipClearTimer = setTimeout(function () {
          if (!tooltip.classList.contains('lc-pdp-share-tooltip--visible')) {
            tooltip.textContent = '';
          }
        }, 200);
      }, 1800);
    }

    btn.addEventListener('click', function () {
      if (navigator.share) {
        navigator.share({
          title: shareTitle,
          url: shareUrl
        }).catch(function () {
          // User cancelled or share failed — do nothing.
        });
      } else {
        // Clipboard copy fallback.
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(shareUrl).then(showTooltip).catch(function () {
            fallbackCopy(shareUrl);
          });
        } else {
          fallbackCopy(shareUrl);
        }
      }
    });

    function fallbackCopy(text) {
      try {
        var ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        ta.style.top  = '-9999px';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        showTooltip();
      } catch (_e) {
        // Fail silently.
      }
    }
  }

  // ─── Initialise on DOM ready ───────────────────────────────────────────────

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initBookmark();
      initShare();
      initSavedPage();
    });
  } else {
    initBookmark();
    initShare();
    initSavedPage();
  }
})();
